import Link from 'next/link';
import LinkReference from './LinkReference';
import { notFound } from 'next/navigation';
import { getLinkTargetAttrs } from '@/utils/getLinkTargetAttrs';

/**
 * Data Object
 * @param {string} linkType Type selected from dropdown (Email, Phone, SMS, External, Internal) if null returns internal link
 * @param {string} linkStyling Tailwind classes for styling the link
 * @param {object} externalLink title and href from link field
 * @param {object} internalLink either a referenced object (with uid + _content_type_uid) OR a direct path string (like /pages/social-impact)
 * @param {string} linkText text that appears for the link
 */
export default function ExternalLink({
  linkType,
  linkStyling,
  externalLink,
  internalLink,
  linkText,
  dataCyAttribute,
  children
}) {
  const prefixMap = {
    Email: 'mailto:',
    Phone: 'tel:',
    Call: 'tel:',
    SMS: 'sms:',
    Text: 'sms:'
  };
  const linkPrefix = prefixMap[linkType] || '';
  let directPathLink = '';

  const externalHref =
    typeof externalLink === 'string'
      ? externalLink
      : typeof externalLink?.href === 'string'
        ? externalLink.href
        : '';
  const externalTitle =
    typeof externalLink === 'object' && typeof externalLink?.title === 'string'
      ? externalLink.title
      : linkText;

  // Validate external href to prevent malformed URLs
  const isValidExternalHref = (href, linkType) => {
    if (!href) return false;

    if (
      linkType === 'Phone' ||
      linkType === 'Call' ||
      linkType === 'SMS' ||
      linkType === 'Text'
    ) {
      return (
        !href.startsWith('/') &&
        (!href.includes('collection') || !href.includes('pages'))
      );
    }
    if (linkType === 'Email') {
      return href.includes('@') && !href.startsWith('/');
    }

    return true;
  };

  const hasValidReferenceLink =
    internalLink?.uid?.trim?.() && internalLink?._content_type_uid;

  if (typeof internalLink === 'string') {
    if (internalLink.startsWith('https://')) {
      try {
        const url = new URL(internalLink);
        directPathLink = url.pathname;
      } catch (error) {
        console.error('Invalid URL:', error);
        return notFound();
      }
    } else if (internalLink.startsWith('/')) {
      directPathLink = internalLink;
    }
  }
  // External link - but only if it's valid for the given linkType
  if (externalHref && isValidExternalHref(externalHref, linkType)) {
    const { target, rel } = getLinkTargetAttrs(
      externalHref,
      linkType === 'External'
    );
    return (
      <a
        title={externalTitle}
        href={`${linkPrefix}${externalHref}`}
        target={target}
        rel={rel}
        className={linkStyling}
        data-cy={dataCyAttribute}
      >
        {children ?? <span>{linkText}</span>}
        <span className="sr-only">(opens in new window)</span>
      </a>
    );
  }

  // Render Contentstack reference link
  if (hasValidReferenceLink) {
    // Does not support adding a data-cy
    return (
      <LinkReference
        reference={internalLink}
        id={internalLink.uid}
        external_url={false}
        prefetch={false}
        className={linkStyling}
      >
        {children ?? linkText}
      </LinkReference>
    );
  }

  // Render static path link
  if (directPathLink) {
    return (
      <Link
        href={directPathLink}
        className={linkStyling}
        data-cy={dataCyAttribute}
      >
        {children ?? <span>{linkText}</span>}
      </Link>
    );
  }

  return null;
}
